import { useTranslation } from '@eti/providers';
import {
  CheckCircle,
  Clock,
  NumberCircleFour,
  NumberCircleThree,
  Warning,
} from '@phosphor-icons/react';
import { RefundCaseCancelReason, RefundCaseStep } from '@eti/schema-types';
import { Stack, vars } from '@etg/wings';
import RefundStatusStep from './RefundStatusStep';
import type { StepStatusType } from './constants';
import { StepStatus } from './constants';
import type { ProviderBookingStepsType, ProviderBookingStepType } from './models';

interface RefundStatusStepsProps {
  cancelReason: RefundCaseCancelReason;
  carrier: string;
  isCC2Carrier: boolean;
  isRyanairProviderBooking: boolean;
  steps: ProviderBookingStepsType;
}

interface StepAdditionalInfo {
  stepIndex: number;
  isLastStep: boolean;
  stepStatus: StepStatusType;
  nextStepStatus: StepStatusType | null;
  isOrphanedFunds: boolean;
  isScheduleChange: boolean;
  isCustomerActionRequired: boolean;
  isCC2CarrierLastStep: boolean;
  shouldBeHidden: boolean;
}

const RefundStatusSteps = ({
  cancelReason,
  steps,
  carrier,
  isCC2Carrier,
  isRyanairProviderBooking,
}: RefundStatusStepsProps) => {
  const { t } = useTranslation();

  const getStepStatus = (step: ProviderBookingStepType, stepIndex: number) => {
    if (step.completed) {
      return StepStatus.Completed;
    }

    if (
      cancelReason === RefundCaseCancelReason.ScheduleChangeRefund &&
      isRyanairProviderBooking &&
      stepIndex === 3
    ) {
      return StepStatus.InProgress;
    }

    if (steps[stepIndex - 1]?.completed) {
      return StepStatus.InProgress;
    }

    return StepStatus.NotStarted;
  };

  const getStepAdditionalInfo = (
    step: ProviderBookingStepType,
    index: number,
  ): StepAdditionalInfo => {
    const isLastStep = index === steps.length - 1;
    const isOrphanedFunds = cancelReason === RefundCaseCancelReason.OrphanedFunds;
    const isScheduleChange = cancelReason === RefundCaseCancelReason.ScheduleChangeRefund;

    return {
      stepIndex: index,
      isLastStep,
      stepStatus: getStepStatus(step, index),
      nextStepStatus: !isLastStep ? getStepStatus(steps[index + 1], index + 1) : null,
      isOrphanedFunds,
      isScheduleChange,
      isCustomerActionRequired: step.step === RefundCaseStep.CustomerRequestedRefundFromProvider,
      isCC2CarrierLastStep: isCC2Carrier && step.step === RefundCaseStep.MoneyReceivedFromProvider,
      shouldBeHidden: isOrphanedFunds && index < 2,
    };
  };

  const getTextKeyPrefix = (step: ProviderBookingStepType['step']) => {
    const prefixes = {
      [RefundCaseStep.RefundRequestReceivedFromCustomer]:
        'MyBookings.RefundStatus.RefundRequestReceivedFromCustomer',
      [RefundCaseStep.RequestedRefundFromProvider]:
        'MyBookings.RefundStatus.RequestedRefundFromProvider',
      [RefundCaseStep.CustomerRequestedRefundFromProvider]:
        'MyBookings.RefundStatus.CustomerRequestedRefundFromProvider',
      [RefundCaseStep.MoneyReceivedFromProvider]:
        'MyBookings.RefundStatus.MoneyReceivedFromProvider',
      [RefundCaseStep.MoneyPayedOutToCustomer]: 'MyBookings.RefundStatus.MoneyPayedOutToCustomer',
    };

    return prefixes[step];
  };

  const getStepTitleForOrphanFunds = (
    step: ProviderBookingStepType['step'],
    stepIndex: number,
    stepStatus: string,
  ) => {
    if (stepIndex === 3 && stepStatus === StepStatus.InProgress) {
      return t(`${getTextKeyPrefix(step)}.OrphanFunds.Pending.Text`);
    }

    if (stepIndex >= 2 && stepStatus === StepStatus.Completed) {
      return t(`${getTextKeyPrefix(step)}.OrphanFunds.Name`);
    }

    return null;
  };

  const getStepDescriptionForOrphanFunds = (
    step: ProviderBookingStepType['step'],
    stepIndex: number,
    stepAdditionalInfo: StepAdditionalInfo,
  ) => {
    const { stepStatus, nextStepStatus } = stepAdditionalInfo;
    if (stepIndex === 2) {
      if (stepStatus === StepStatus.Completed && nextStepStatus === StepStatus.Completed) {
        return t(`${getTextKeyPrefix(step)}.OrphanFunds.SuccessPayout.Text`);
      }
      if (stepStatus === StepStatus.Completed && nextStepStatus === StepStatus.InProgress) {
        return t(`${getTextKeyPrefix(step)}.OrphanFunds.PendingPayout.Text`);
      }
    }

    if (stepIndex === 3) {
      if (stepStatus === StepStatus.Completed) {
        return t(`${getTextKeyPrefix(step)}.OrphanFunds.Success.Text`);
      }
    }

    return null;
  };

  const getStepTitle = (
    step: ProviderBookingStepType['step'],
    stepAdditionalInfo: StepAdditionalInfo,
  ) => {
    const { stepIndex, isOrphanedFunds, stepStatus, isCustomerActionRequired } = stepAdditionalInfo;

    if (isOrphanedFunds) {
      return getStepTitleForOrphanFunds(step, stepIndex, stepStatus);
    }

    if (
      stepStatus === StepStatus.InProgress &&
      cancelReason === RefundCaseCancelReason.ScheduleChangeRefund &&
      isRyanairProviderBooking
    ) {
      return t('MyBookings.RefundStatus.MoneyPayedOutToCustomer.Ryanair.Waiting');
    }

    if (stepStatus === StepStatus.InProgress && !isCustomerActionRequired) {
      return t(`${getTextKeyPrefix(step)}.Waiting`, carrier);
    }

    return t(`${getTextKeyPrefix(step)}.Name`, carrier);
  };

  const getStepDescription = (
    step: ProviderBookingStepType['step'],
    stepAdditionalInfo: StepAdditionalInfo,
  ) => {
    const {
      stepIndex,
      isOrphanedFunds,
      isScheduleChange,
      stepStatus,
      isCC2CarrierLastStep,
      isCustomerActionRequired,
      isLastStep,
    } = stepAdditionalInfo;

    if (isOrphanedFunds) {
      return getStepDescriptionForOrphanFunds(step, stepIndex, stepAdditionalInfo);
    }
    switch (stepStatus) {
      case StepStatus.Completed:
        if (isScheduleChange && isRyanairProviderBooking && isLastStep) {
          return t(
            'MyBookings.RefundStatus.ScheduleChangeRefund.MoneyPayedOutToCustomer.Description',
          );
        }
        return t(`${getTextKeyPrefix(step)}.Description`, carrier);
      case StepStatus.InProgress: {
        if (isCustomerActionRequired) {
          return t(`${getTextKeyPrefix(step)}.Waiting`, carrier);
        }
        if (isCC2CarrierLastStep) {
          return t(`${getTextKeyPrefix(step)}.CC2Carrier.Description`, carrier);
        }

        return null;
      }
      default:
        return null;
    }
  };

  const getStepIcon = (stepAdditionalInfo: StepAdditionalInfo) => {
    const { stepIndex, stepStatus, isCC2CarrierLastStep, isCustomerActionRequired } =
      stepAdditionalInfo;
    switch (stepStatus) {
      case StepStatus.Completed:
        return (
          <CheckCircle
            alt={t('ScreenReader.CompletedIcon.AriaLabel.Text')}
            color={vars.colors.signal.positive.light}
            data-testid={`refund-status-step-icon-${StepStatus.Completed}`}
            size={22}
            weight="fill"
          />
        );
      case StepStatus.InProgress: {
        if (isCustomerActionRequired || isCC2CarrierLastStep) {
          return (
            <Warning
              alt={t('ScreenReader.WarningIcon.AriaLabel.Text')}
              color={vars.colors.signal.caution.light}
              data-testid={`refund-status-step-icon-${StepStatus.InProgress}-warning`}
              size={22}
              weight="fill"
            />
          );
        }

        return (
          <Clock
            alt={t('ScreenReader.InProgressIcon.AriaLabel.Text')}
            color={vars.colors.signal.information.light}
            data-testid={`refund-status-step-icon-${StepStatus.InProgress}-info`}
            size={22}
            weight="fill"
          />
        );
      }
      default: // StepStatus.NotStarted
        switch (stepIndex) {
          case 2:
            return (
              <NumberCircleThree
                alt={t('ScreenReader.NotStartedIcon.AriaLabel.Text')}
                color="#d9d9d9"
                data-testid={`refund-status-step-icon-${StepStatus.NotStarted}-${stepIndex + 1}`}
                size={22}
                weight="fill"
              />
            );
          case 3:
            return (
              <NumberCircleFour
                alt={t('ScreenReader.NotStartedIcon.AriaLabel.Text')}
                color="#d9d9d9"
                data-testid={`refund-status-step-icon-${StepStatus.NotStarted}-${stepIndex + 1}`}
                size={22}
                weight="fill"
              />
            );
          default:
            return null;
        }
    }
  };

  return (
    <Stack role="list" spacing={0}>
      {steps.map((step, stepIndex) => {
        const stepAdditionalInfo = getStepAdditionalInfo(step, stepIndex);
        if (stepAdditionalInfo.shouldBeHidden) {
          return null;
        }

        if (
          stepAdditionalInfo.isScheduleChange &&
          isRyanairProviderBooking &&
          (stepIndex === 1 || stepIndex === 2)
        ) {
          return null;
        }

        return (
          <RefundStatusStep
            data-testid={`refund-status-step-${step.step}`}
            description={getStepDescription(step.step, stepAdditionalInfo)}
            isCustomerActionRequired={stepAdditionalInfo.isCustomerActionRequired}
            isLastStep={stepAdditionalInfo.isLastStep}
            key={step.step}
            nextStepStatus={stepAdditionalInfo.nextStepStatus}
            stepIcon={getStepIcon(stepAdditionalInfo)}
            stepStatus={stepAdditionalInfo.stepStatus}
            title={getStepTitle(step.step, stepAdditionalInfo)}
          />
        );
      })}
    </Stack>
  );
};

export default RefundStatusSteps;
